import type { FunctionComponent } from 'react';
import { Toaster } from 'react-hot-toast';
import { QueryClientProvider } from 'react-query';
import { Routes as RouterRoutes, Route } from 'react-router-dom';

import {
    GlobalThemeProvider,
    GlobalStyle,
    NavigationContextProvider,
    AuthContextProvider,
    PrivateLayout,
} from '@catalyze/ui';
import { createQueryClient } from 'api/createQueryClient';
import { useGetUserQuery } from 'api/users/getUser';
import { signInUser } from 'api/users/signInUser';
import { Cookies } from 'constants/cookies';
import { NAVIGATION_ITEMS } from 'constants/navigation';
import { ChangePassword } from 'features/CreatePassword/pages/ChangePassword';
import { CreatePassword } from 'features/CreatePassword/pages/CreatePassword';
import { Profile } from 'features/Profile/pages/Profile';
import { Active } from 'features/RealEstate/pages/Active';
import { Archived } from 'features/RealEstate/pages/Archived';
import { Contacted } from 'features/RealEstate/pages/Contacted';
import { Listed } from 'features/RealEstate/pages/Listed';
import { Overview } from 'features/RealEstate/pages/Overview';
import { PropertyDetail } from 'features/RealEstate/pages/PropertyDetail/pages/PropertyDetail';
import { Recommended } from 'features/RealEstate/pages/Recommended';
import { Search } from 'features/RealEstate/pages/Search';
import { Sold } from 'features/RealEstate/pages/Sold';
import { ResetPassword } from 'features/ResetPassword/pages/ResetPassword';
import { SignIn } from 'features/SignIn/pages/SignIn';
import { SignUp } from 'features/SignUp/pages/SignUp';
import { SignUpApproval } from 'features/SignUp/pages/SignUpApproval';
import { Subscribe } from 'features/Subscribe/pages/Subscribe';
import { Subscribed } from 'features/Subscribed/pages/Subscribed';
import { SubscribedV2 } from 'features/SubscribedV2/SubscribedV2';
import { Subscription } from 'features/Subscription/pages/Subscription';
import { ZipCheck } from 'features/ZipCheck/pages/ZipCheck';

import { Routes } from './constants/routes';
import { NotFound } from './features/NotFound/pages/NotFound';
import { FilterContextProvider } from './features/RealEstate/context/FilterContext';

const client = createQueryClient();

export const App: FunctionComponent = () => (
    <GlobalThemeProvider>
        <QueryClientProvider client={client}>
            <NavigationContextProvider
                navItems={NAVIGATION_ITEMS}
                homepageUrl={Routes.OVERVIEW}
                unauthorizedUrl={Routes.SIGN_IN}
                profileUrl={Routes.PROFILE}
                searchUrl={Routes.SEARCH}
            >
                <AuthContextProvider
                    logRocketId={process.env.REACT_APP_REAL_ESTATE_LOGROCKET_ID}
                    useGetUserQuery={useGetUserQuery}
                    signInUser={signInUser}
                    cookies={{
                        accessToken: Cookies.ACCESS_TOKEN,
                        refreshToken: Cookies.REFRESH_TOKEN,
                    }}
                    signInPath={Routes.SIGN_IN}
                >
                    <FilterContextProvider>
                        <GlobalStyle />
                        <Toaster position="bottom-right" reverseOrder={false} />
                        <RouterRoutes>
                            {/* Private routes */}
                            <Route
                                path={Routes.OVERVIEW}
                                element={
                                    <PrivateLayout>
                                        <Overview />
                                    </PrivateLayout>
                                }
                            />

                            <Route
                                path={Routes.SEARCH}
                                element={
                                    <PrivateLayout>
                                        <Search />
                                    </PrivateLayout>
                                }
                            />

                            <Route
                                path={Routes.RECOMMENDED}
                                element={
                                    <PrivateLayout>
                                        <Recommended />
                                    </PrivateLayout>
                                }
                            />
                            <Route
                                path={Routes.CONTACTED}
                                element={
                                    <PrivateLayout>
                                        <Contacted />
                                    </PrivateLayout>
                                }
                            />
                            <Route
                                path={Routes.ACTIVE_CONVERSATIONS}
                                element={
                                    <PrivateLayout>
                                        <Active />
                                    </PrivateLayout>
                                }
                            />
                            <Route
                                path={Routes.LISTED}
                                element={
                                    <PrivateLayout>
                                        <Listed />
                                    </PrivateLayout>
                                }
                            />
                            <Route
                                path={Routes.ARCHIVE}
                                element={
                                    <PrivateLayout>
                                        <Archived />
                                    </PrivateLayout>
                                }
                            />
                            <Route
                                path={Routes.SOLD}
                                element={
                                    <PrivateLayout>
                                        <Sold />
                                    </PrivateLayout>
                                }
                            />

                            <Route
                                path={Routes.PROPERTY}
                                element={
                                    <PrivateLayout>
                                        <PropertyDetail />
                                    </PrivateLayout>
                                }
                            />

                            <Route
                                path={Routes.PROFILE}
                                element={
                                    <PrivateLayout>
                                        <Profile />
                                    </PrivateLayout>
                                }
                            />

                            {/* Public routes */}
                            <Route path={Routes.SIGN_IN} element={<SignIn />} />

                            <Route
                                path={Routes.CREATE_PASSWORD}
                                element={<CreatePassword />}
                            />
                            <Route
                                path={Routes.CHANGE_PASSWORD}
                                element={<ChangePassword />}
                            />
                            <Route
                                path={Routes.SUBSCRIPTION}
                                element={<Subscription />}
                            />
                            <Route
                                path={Routes.SUBSCRIBED}
                                element={<Subscribed />}
                            />
                            <Route
                                path={Routes.SUBSCRIBE}
                                element={<Subscribe />}
                            />
                            <Route
                                path={Routes.SUBSCRIBED_V2}
                                element={<SubscribedV2 />}
                            />
                            <Route
                                path={Routes.ZIP_CHECK}
                                element={<ZipCheck />}
                            />

                            <Route path={Routes.SIGN_UP} element={<SignUp />} />

                            <Route
                                path={Routes.RESET_PASSWORD}
                                element={<ResetPassword />}
                            />

                            <Route
                                path={Routes.SIGN_UP_APPROVAL}
                                element={<SignUpApproval />}
                            />

                            {/* Default */}
                            <Route
                                path={Routes.NOT_FOUND}
                                element={
                                    <PrivateLayout>
                                        <NotFound />
                                    </PrivateLayout>
                                }
                            />
                        </RouterRoutes>
                    </FilterContextProvider>
                </AuthContextProvider>
            </NavigationContextProvider>
        </QueryClientProvider>
    </GlobalThemeProvider>
);
